import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { DollarSign, Users, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useAuthStore } from '../stores/authStore';

const AffiliateInfoPage: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  // Auto-redirect to affiliate dashboard if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/affiliate', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Structured data for SEO (JSON-LD)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Affiliate Program - Earn 50% + 10% Lifetime Commission | Metapic",
    "description": "Join Metapic's lifetime affiliate program. Earn 50% on first month + 10% recurring commission on ALL future purchases FOREVER. Passive income opportunity for content creators.",
    "url": "https://metapic.app/affiliate-program",
    "mainEntity": {
      "@type": "OfferCatalog",
      "name": "Metapic Lifetime Affiliate Program",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Lifetime Affiliate Partnership",
            "description": "Earn 50% commission on first month + 10% lifetime recurring commission on all future purchases"
          }
        }
      ]
    },
    "publisher": {
      "@type": "Organization",
      "name": "Metapic",
      "url": "https://metapic.app"
    }
  };

  return (
    <Layout>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Lifetime Affiliate Program - Earn 50% + 10% Forever | Metapic</title>
        <meta name="title" content="Lifetime Affiliate Program - Earn 50% + 10% Forever | Metapic" />
        <meta name="description" content="Join Metapic's lifetime affiliate program: Earn 50% on first month + 10% recurring commission on ALL future purchases FOREVER. True passive income for content creators, photographers, and marketers." />
        <meta name="keywords" content="lifetime affiliate program, recurring commission, passive income affiliate, lifetime commissions, affiliate marketing, earn forever, subscription affiliate, content creator income, photography affiliate, AI tools affiliate, recurring revenue, metapic affiliate" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://metapic.app/affiliate-program" />
        <meta property="og:title" content="Lifetime Affiliate Program - Earn 50% + 10% Forever | Metapic" />
        <meta property="og:description" content="Earn 50% on first month + 10% recurring commission FOREVER on all renewals and purchases. True passive income opportunity." />
        <meta property="og:image" content="https://metapic.app/og-affiliate.jpg" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://metapic.app/affiliate-program" />
        <meta property="twitter:title" content="Lifetime Affiliate Program - Earn 50% + 10% Forever | Metapic" />
        <meta property="twitter:description" content="Earn 50% on first month + 10% recurring commission FOREVER on all renewals and purchases. True passive income opportunity." />
        <meta property="twitter:image" content="https://metapic.app/og-affiliate.jpg" />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="author" content="Metapic" />
        <link rel="canonical" href="https://metapic.app/affiliate-program" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        {/* Hero Section */}
        <div className="container mx-auto px-4 py-16 max-w-6xl">
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-green-100 to-blue-100 px-4 py-2 rounded-full text-sm font-semibold text-green-700 mb-4">
              NEW: Lifetime Recurring Commissions!
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Earn 50% First Month + 10% Lifetime
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Join Metapic's revolutionary lifetime affiliate program. Earn <strong>50% commission on the first month</strong> when someone signs up, 
              plus <strong>10% recurring commission on ALL future purchases FOREVER</strong>. True passive income for content creators, 
              photographers, and marketers who want to earn while they sleep.
            </p>
            
            {/* CTA Button */}
            {isAuthenticated ? (
              <Link
                to="/affiliate"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
              >
                Apply Now <ArrowRight className="h-5 w-5" />
              </Link>
            ) : (
              <Link
                to="/login?redirect=/affiliate"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
              >
                Login to Apply <ArrowRight className="h-5 w-5" />
              </Link>
            )}
            
            {/* Minimum Payout Info */}
            <div className="mt-6 flex items-center justify-center gap-4 flex-wrap">
              <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
                💰 Minimum Payout: $20
              </div>
              <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">
                ⚡ Fast Payouts via PayPal
              </div>
              <div className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-semibold">
                🚀 Tier-Based Rewards
              </div>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <article className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">50% on First Month</h2>
              <p className="text-gray-600">
                Earn 50% commission on the first month equivalent when someone signs up. For yearly plans, you get 50% of the monthly price. 
                For monthly plans, you get 50% of the full amount. Generous upfront earnings to kickstart your income.
              </p>
            </article>

            <article className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">10% Lifetime Recurring</h2>
              <p className="text-gray-600">
                Earn 10% commission on EVERY future purchase your referrals make - renewals, credit top-ups, upgrades. 
                As long as they use Metapic, you earn. True passive income that compounds over time.
              </p>
            </article>

            <article className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">No Limits, No Expiry</h2>
              <p className="text-gray-600">
                Your recurring commissions never expire. Refer once, earn forever. No cap on how much you can earn. 
                Build a portfolio of recurring income streams that grow month after month, year after year.
              </p>
            </article>
          </div>

          {/* How It Works */}
          <section className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">How Our Affiliate Program Works</h2>
            
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  1
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Apply</h3>
                <p className="text-sm text-gray-600">Sign up and submit your affiliate application</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  2
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Get Approved</h3>
                <p className="text-sm text-gray-600">We review your application within 24-48 hours</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  3
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Share</h3>
                <p className="text-sm text-gray-600">Share your unique referral link with your audience</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  4
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Earn</h3>
                <p className="text-sm text-gray-600">Get 50% on first month + 10% on every future purchase</p>
              </div>
            </div>
          </section>

          {/* Affiliate Tier Structure */}
          <section className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">📊 Affiliate Tier Structure</h2>
            <p className="text-center text-gray-600 mb-8">Unlock higher commissions as your total earnings grow! The more you earn, the more you keep.</p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Basic Tier */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border-2 border-gray-200">
                <div className="text-3xl mb-2">🌟</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Basic Tier</h3>
                <div className="text-sm text-gray-600 mb-4">$0 - $2,000 total earnings</div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">First Payment:</span>
                    <span className="font-bold text-gray-900">50%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Recurring:</span>
                    <span className="font-bold text-green-600">10% lifetime!</span>
                  </div>
                </div>
              </div>

              {/* Silver Tier */}
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl p-6 border-2 border-gray-400 relative">
                <div className="absolute -top-3 -right-3 bg-gray-500 text-white text-xs px-3 py-1 rounded-full font-semibold">
                  +3%
                </div>
                <div className="text-3xl mb-2">🥈</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Silver Tier</h3>
                <div className="text-sm text-gray-600 mb-4">$2,001 - $5,000 total earnings</div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">First Payment:</span>
                    <span className="font-bold text-gray-900">53%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Recurring:</span>
                    <span className="font-bold text-green-600">12% lifetime!</span>
                  </div>
                </div>
              </div>

              {/* Gold Tier */}
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6 border-2 border-yellow-400 relative">
                <div className="absolute -top-3 -right-3 bg-yellow-500 text-white text-xs px-3 py-1 rounded-full font-semibold">
                  +5%
                </div>
                <div className="text-3xl mb-2">🥇</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Gold Tier</h3>
                <div className="text-sm text-gray-600 mb-4">$5,001 - $10,000 total earnings</div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">First Payment:</span>
                    <span className="font-bold text-gray-900">55%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Recurring:</span>
                    <span className="font-bold text-green-600">15% lifetime!</span>
                  </div>
                </div>
              </div>

              {/* Platinum Tier */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border-2 border-purple-400 relative">
                <div className="absolute -top-3 -right-3 bg-purple-500 text-white text-xs px-3 py-1 rounded-full font-semibold">
                  +10%
                </div>
                <div className="text-3xl mb-2">💎</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Platinum Tier</h3>
                <div className="text-sm text-gray-600 mb-4">$10,001+ total earnings</div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">First Payment:</span>
                    <span className="font-bold text-gray-900">60%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Recurring:</span>
                    <span className="font-bold text-green-600">20% lifetime!</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-gray-700 text-center">
                💡 <strong>Tier upgrades are automatic!</strong> As your total earnings grow, your commission rates increase on all future earnings. 
                Keep promoting to unlock higher tiers and maximize your passive income!
              </p>
            </div>
          </section>

          {/* Commission Structure */}
          <section className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl p-8 text-white mb-16">
            <h2 className="text-3xl font-bold mb-2 text-center">How Affiliates Earn</h2>
            <p className="text-center text-white/90 mb-8 text-lg">Two ways to earn: Upfront + Lifetime Recurring</p>
            
            {/* First Month Commissions */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-center">Get 50% on First Month</h3>
              <p className="text-center text-white/90 mb-2">When someone signs up using your link</p>
              <p className="text-center text-white/80 text-sm max-w-2xl mx-auto">
                Earn generous upfront commission based on your tier level (50%-60% of first month value)
              </p>
            </div>

            {/* Lifetime Recurring Commissions */}
            <div className="border-t border-white/20 pt-8">
              <h3 className="text-2xl font-bold mb-4 text-center">PLUS 10-20% on Every Transaction (Lifetime!)</h3>
              <p className="text-center text-white/90 text-sm max-w-2xl mx-auto">
                Recurring commission on ALL future purchases - renewals, credit top-ups, upgrades. As long as they use Metapic, you earn!
              </p>
            </div>
          </section>

          {/* Who Should Join */}
          <section className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Who Should Join Our Affiliate Program</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Content Creators</h3>
                  <p className="text-gray-600">YouTubers, bloggers, and influencers who create content about photography, e-commerce, or digital tools</p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Stock Photographers</h3>
                  <p className="text-gray-600">Professional and amateur photographers who want to earn extra income while helping others</p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Marketing Agencies</h3>
                  <p className="text-gray-600">Agencies that recommend tools to clients and want to earn commission on referrals</p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Tech Reviewers</h3>
                  <p className="text-gray-600">Tech enthusiasts who review SaaS products and AI tools for their audience</p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="bg-gray-50 rounded-xl p-8 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Affiliate Program FAQ</h2>
            
            <div className="space-y-6 max-w-3xl mx-auto">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How much can I earn?</h3>
                <p className="text-gray-600">
                  You earn in two ways: <strong>50% of the first month</strong> when someone signs up, plus <strong>10% on ALL future purchases</strong> they make forever. 
                  For example, if someone signs up for Business Yearly ($1350), you earn $56.25 upfront. Then every year they renew, you earn $135. 
                  Plus you earn 10% on any credit top-ups they buy. This is true passive income that compounds over time.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">What counts as a "future purchase"?</h3>
                <p className="text-gray-600">
                  You earn 10% on ALL transactions your referrals make, including: subscription renewals (monthly or yearly), 
                  credit top-ups ($5, $10, $20), plan upgrades, and any other purchases. As long as they're your referral, you earn on everything!
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How long do I earn recurring commissions?</h3>
                <p className="text-gray-600">
                  Forever! There's no expiration on lifetime commissions. As long as your referral continues using Metapic and making purchases, 
                  you continue earning 10% on every transaction. This could mean years of passive income from a single referral.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">When do I get paid?</h3>
                <p className="text-gray-600">
                  You can request a payout once your approved balance reaches <strong>$20 minimum</strong>. Payouts are processed manually within 2-3 business days via PayPal. 
                  All commissions (first month and recurring) go through admin approval first to prevent fraud.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How does the first month commission work for yearly plans?</h3>
                <p className="text-gray-600">
                  For yearly plans, we calculate the monthly equivalent and give you 50% of that. For example: Business Yearly is $1350/year = $112.50/month. 
                  You earn 50% × $112.50 = $56.25. This keeps the commission fair and sustainable. For monthly plans, you earn 50% of the full monthly price.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How long does approval take?</h3>
                <p className="text-gray-600">
                  Most applications are reviewed within 24-48 hours. We'll notify you via email once your application is approved or if we need more information.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How does referral tracking work?</h3>
                <p className="text-gray-600">
                  When someone clicks your referral link, we store a tracking cookie and local storage on their browser for 30 days. 
                  They can browse, leave, and come back - as long as they sign up within 30 days using the same browser, you get credit. 
                  Once they sign up, they're your referral forever for all future commissions!
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">What makes this different from other affiliate programs?</h3>
                <p className="text-gray-600">
                  Most affiliate programs only pay once on the first sale. We pay you <strong>forever</strong>. Every time your referral renews their subscription 
                  or buys more credits, you earn 10%. This creates true passive income - refer once, earn for years. It's like building a portfolio of 
                  recurring revenue streams.
                </p>
              </div>
            </div>
          </section>

          {/* Why This Rocks Section */}
          <section className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8 mb-16 border border-green-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Why This Rocks</h2>
            <p className="text-center text-gray-600 mb-8">The smartest affiliate program you'll ever join</p>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="flex gap-4">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">✅ True Passive Income</h3>
                  <p className="text-gray-600">Earn from renewals automatically, even while you sleep. No extra work required after the initial referral.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">✅ Lifetime Earnings</h3>
                  <p className="text-gray-600">As long as they use Metapic, you earn 10% on everything. One referral = years of income.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">✅ Unlimited Potential</h3>
                  <p className="text-gray-600">No cap on earnings, no limit on referrals. Build a portfolio of recurring income streams.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">✅ Simple & Fair</h3>
                  <p className="text-gray-600">Easy to understand, generous rates. 50% upfront + 10% forever. No complicated tiers or quotas.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">✅ Quality Over Quantity</h3>
                  <p className="text-gray-600">Focus on referring engaged users who will renew. Long-term customers = long-term income for you.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">✅ Compounds Over Time</h3>
                  <p className="text-gray-600">Each new referral adds to your recurring income base. 10 referrals this year = more income next year!</p>
                </div>
              </div>
            </div>
          </section>

          {/* Final CTA */}
          <section className="text-center bg-white rounded-xl p-12 shadow-lg border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Build Your Passive Income Stream?</h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Join our lifetime affiliate program and start earning 50% upfront + 10% forever on every referral. 
              Build recurring revenue that grows month after month, year after year.
            </p>
            
            {isAuthenticated ? (
              <Link
                to="/affiliate"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl text-lg"
              >
                Apply for Affiliate Program <ArrowRight className="h-5 w-5" />
              </Link>
            ) : (
              <Link
                to="/login?redirect=/affiliate"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl text-lg"
              >
                Login to Apply <ArrowRight className="h-5 w-5" />
              </Link>
            )}
            
            <p className="text-sm text-gray-500 mt-4">
              Already an affiliate? <Link to="/affiliate" className="text-blue-600 hover:underline">Go to Dashboard</Link>
            </p>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default AffiliateInfoPage;
